import { z } from "zod";
import { fetchEventStream } from "../api/eventStream.js";
import type {
  ClientAssistantChatMessage,
  ClientChatMessage,
  ClientToolChatMessage,
} from "./types.js";

export async function* fetchChatCompletion(
  url: string | URL,
  options: {
    headers?: Record<string, string>;
    body: unknown;
    signal: AbortSignal;
    onEvent?: (event: string | undefined, data: string) => void;
  },
): AsyncGenerator<ClientChatMessage, void, void> {
  let loadingMessage: ClientAssistantChatMessage | undefined;

  for await (const { event, data } of fetchEventStream(url, options)) {
    switch (event) {
      case "message.started": {
        const message = z
          .object({
            id: z.string(),
            role: z.enum(["assistant"]),
            created_at: z.string(),
          })
          .parse(JSON.parse(data));

        loadingMessage = {
          ...message,
          createdAt: message.created_at,
          content: "",
          isLoading: true,
        };
        yield loadingMessage;
        break;
      }
      case "message.content_delta": {
        const contentDelta = z.string().parse(JSON.parse(data));

        if (loadingMessage) {
          loadingMessage = {
            ...loadingMessage,
            content: loadingMessage.content + contentDelta,
          };
          yield loadingMessage;
        }
        break;
      }
      case "message.completed": {
        if (loadingMessage) {
          loadingMessage = {
            ...loadingMessage,
            isLoading: false,
          };
          yield loadingMessage;
          loadingMessage = undefined;
        }
        break;
      }
      case "tool_message.completed": {
        const toolMessage = z
          .object({
            id: z.string(),
            role: z.enum(["tool"]),
            tool_call_id: z.string(),
            content: z.string(),
            alternative_content: z.string().optional(),
            created_at: z.string(),
            in_reply_to_id: z.string(),
          })
          .parse(JSON.parse(data));

        const clientToolMessage: ClientToolChatMessage = {
          id: toolMessage.id,
          role: toolMessage.role,
          toolCallId: toolMessage.tool_call_id,
          content: toolMessage.content,
          alternativeContent: toolMessage.alternative_content,
          createdAt: toolMessage.created_at,
          inReplyToId: toolMessage.in_reply_to_id,
        };
        yield clientToolMessage;
        break;
      }
      default: {
        options.onEvent?.(event, data);
        break;
      }
    }
  }
}
